// 影像牆（plan/vms-web-v0.md §5.5 LIVE-01/02/03/04/08）：1 / 4 / 6 / 8 / 9 / 10 / 13 / 16 / 25 / 36 格、跨家戶 / 場域混合、點選加入、放大單格。
//   - 碼流：格子 ≤ 1/4 畫面要子碼流、大格與放大要主碼流（start_viewing 帶 stream；設備端子碼流待韌體）
//   - 解碼預算：同時即時解碼上限（依 CPU 核數，預設 16），其餘格子顯示「預覽」，點了才轉即時
// 目前的牆存在瀏覽器（localStorage，個人便利）；具名版面存伺服器 vms.layouts（個人 / org 共用，LayoutMenu.tsx）。
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDismiss } from '../../components/useDismiss';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../../lib/AppContext';
import { useDevices, useDevicesIn } from '../../lib/useDevices';
import { whyNot } from '../../lib/denial';
import { can, fullName, type VmsDevice } from '../../lib/devices';
import { LiveTile } from './LiveTile';
import { DENIED, LayoutMenu, layoutWall, useServerLayouts } from './LayoutMenu';
import { TourControl } from './TourControl';
import { DeviceControls } from '../devices/DeviceControls';
import { GRID_KEYS, gridKeyOf, ignoreHotkey, useMedia } from '../../lib/hotkeys';
import { controlCaps } from '../../lib/caps';
import { parseTour, type TourSpec } from './tour';
import { CUSTOM_MAX, GRID_SIZES, decodeBudget, dropOnCell, liveCells, streamFor, wallOf, type GridSize, type Wall } from './wall';
import { registerWallPopup } from '../../lib/alarmPopup';
import { useScope } from '../../lib/scope';
import { DND_SCOPE } from '../orgtree/dnd';
import { onPlaceCamera } from '../orgtree/placeCamera';
import { layoutKey, multiScreen } from '../../lib/multiScreen';
import { useIsAlarmWindow, useMultiScreenVersion } from '../../lib/useMultiScreen';
import { LIVE_LAYOUT_KEY, PopoutButton, RestoreButton } from './MultiScreen';
import { scopeNodeName } from '../shell/Shell';
import { nodePath } from '../../lib/myDevices';
import { PATH_SEP } from '../../lib/orgRoot';
import { useDevicePath } from '../devices/path';

export { GRID_SIZES, type GridSize } from './wall';

interface SavedLayout {
  size: number;
  /** 自訂格線的欄數（LIVE-01）；沒有 = 預設版面 */
  cols?: number;
  /** 輪巡設定（LIVE-05） */
  tour?: TourSpec | null;
  cells: (string | null)[]; // VmsDevice.key
  /** 切到較小版型時放不下的攝影機（依原順序）；切回大版型時補回空格（B-03、SPEC #vms-web-437 格位規則） */
  overflow?: string[];
  /** 這面牆是〔填滿〕依範圍分頁排出來的第幾頁（P0-5：只有填滿過才顯示「第 n / N 頁」；換版型、手動改就不算） */
  page?: number;
}

/** 拖放的資料型別（左側組織樹拖攝影機、牆上拖格子；組織樹的節點 / 群組拖進來 = 依版面自動分頁的影像牆）。 */
const DND_CAM = 'application/x-kinio-cam';
const DND_CELL = 'application/x-kinio-cell';

function dropSource(e: React.DragEvent): { cam: string } | { cell: number } | null {
  const cam = e.dataTransfer.getData(DND_CAM);
  if (cam) return { cam };
  const cell = e.dataTransfer.getData(DND_CELL);
  return cell !== '' && Number.isInteger(Number(cell)) ? { cell: Number(cell) } : null;
}

// 主視窗用舊鍵；彈出的每面牆各一份（LIVE-10）
const STORAGE_KEY = layoutKey(LIVE_LAYOUT_KEY);

function loadLayout(): SavedLayout | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const l = JSON.parse(raw) as SavedLayout;
    if (!Array.isArray(l.cells) || typeof l.size !== 'number') return null;
    const w = wallOf(l.size, l.cols);
    const overflow = Array.isArray(l.overflow) ? l.overflow.filter((k): k is string => typeof k === 'string') : [];
    const page = typeof l.page === 'number' && l.page >= 0 ? l.page : undefined;
    return { size: w.size, cols: w.custom ? w.cols : undefined, cells: l.cells, tour: parseTour(l.tour), overflow, page };
  } catch {
    return null;
  }
}

function saveLayout(l: SavedLayout): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(l));
  } catch {
    /* 無 storage */
  }
}

/** 調整格數：保留前面的格子，多的補空、少的截掉。 */
export function resize(cells: (string | null)[], size: number): (string | null)[] {
  const out = cells.slice(0, size);
  while (out.length < size) out.push(null);
  return out;
}

/**
 * 換版型（B-03、SPEC #vms-web-437）：攝影機不因為換版型就不見。
 *   - 放得下（後面被裁掉的格子都是空的）→ 位置不動
 *   - 放不下 → 先把有攝影機的格子依原順序往前壓縮，再裁；裁掉的放進 overflow
 *   - 之前放不下的（overflow）有空格就依序補回（切回大版型就回來）
 */
export function resizeWall(cells: (string | null)[], size: number, overflow: string[] = []): { cells: (string | null)[]; overflow: string[] } {
  const all = [...cells];
  while (all.length < size) all.push(null);
  let out: (string | null)[];
  let rest: string[] = [];
  if (all.slice(size).some(Boolean)) {
    const keys = all.filter((k): k is string => !!k);
    out = keys.slice(0, size);
    rest = keys.slice(size).filter((k) => k !== DENIED);
    while (out.length < size) out.push(null);
  } else {
    out = all.slice(0, size);
  }
  const queue = [...rest, ...overflow.filter((k) => !out.includes(k) && !rest.includes(k))];
  for (let i = 0; i < out.length && queue.length; i++) if (!out[i]) out[i] = queue.shift()!;
  return { cells: out, overflow: queue };
}

/**
 * 影像牆提示列的畫面數一句話（牆上與單路放大同一個函式；第十二輪 B12-12、第十三輪 B13-05）：
 * 有放不下的（下一頁還有）→「這個範圍有 9 個畫面：這一頁 4 個，下一頁還有 5 個」；放得下 →「這個範圍有 N 個畫面，牆上放了其中 X 個」。
 */
export function wallCountText(t: (k: string, o?: Record<string, unknown>) => string, n: number, onWall: number, rest: number): string {
  return rest > 0 ? t('ot.you_can_view_wall_paged', { n, x: onWall, rest }) : t('ot.you_can_view_wall', { n, x: onWall });
}

/** 縮小版型前的牆（含空格的原始格位）：切回放得下的版型、而且牆上還是同一批攝影機時照原樣還原（VMS UX 評審第三輪 R3-P0-1、B3-22 走查：16 → 4 → 16 空格被壓縮掉）。 */
export interface ShrinkMemo {
  cells: (string | null)[];
  keys: string;
}

export function wallKeys(cells: (string | null)[], overflow: string[] = []): string {
  return [...cells.filter((k): k is string => !!k), ...overflow].sort().join('|');
}

/** 換版型：先看能不能照縮小前的格位還原，不能才保序壓縮（resizeWall）。回傳新的 memo（null = 不用記了）。 */
export function switchCells(
  cells: (string | null)[],
  size: number,
  overflow: string[],
  memo: ShrinkMemo | null,
): { cells: (string | null)[]; overflow: string[]; memo: ShrinkMemo | null } {
  const keys = wallKeys(cells, overflow);
  const m = memo && memo.keys === keys ? memo : null;
  if (m) {
    const last = m.cells.reduce((acc, k, i) => (k ? i : acc), -1);
    if (last < size) {
      const out = m.cells.slice(0, size);
      while (out.length < size) out.push(null);
      return { cells: out, overflow: [], memo: null };
    }
  }
  const shrinking = cells.slice(size).some(Boolean);
  const r = resizeWall(cells, size, overflow);
  return { ...r, memo: m ?? (shrinking ? { cells: [...cells], keys } : null) };
}

/**
 * 把攝影機放進第一個「沒有可播攝影機」的格子（B-03：格子可能存著已不在範圍、看起來是空的 key）；
 * 已在牆上就不重複放（at = 它所在的格子，呼叫端把它選取）。沒有空格就放到目前選取的格子（取代；沒選就最後一格），
 * 換下來的回傳在 bumped（放進 overflow）。SPEC #vms-web-437 LIVE-01 格位規則。
 */
export function place(cells: (string | null)[], key: string, playable: (k: string) => boolean = () => true, selected: number | null = null): (string | null)[] {
  return placeWall(cells, key, playable, selected).cells;
}

export function placeWall(
  cells: (string | null)[],
  key: string,
  playable: (k: string) => boolean = () => true,
  selected: number | null = null,
): { cells: (string | null)[]; bumped: string | null; at: number } {
  const was = cells.indexOf(key);
  if (was >= 0) return { cells, bumped: null, at: was };
  const out = [...cells];
  const i = out.findIndex((k) => !k || !playable(k));
  const at = i >= 0 ? i : selected !== null && selected >= 0 && selected < out.length ? selected : out.length - 1;
  const bumped = i >= 0 ? null : out[at];
  out[at] = key;
  return { cells: out, bumped, at };
}

/**
 * 載入或切範圍時清掉「已不在我能看的清單裡」的格子（權限沒了、設備被移除；SPEC LIVE-01 格位規則）。
 * known = 我所有看得到的攝影機 key（不限目前範圍——切範圍不清空牆）；DENIED 格（存版面時沒權限的）留著。
 */
export function pruneWall(cells: (string | null)[], overflow: string[], known: (k: string) => boolean): { cells: (string | null)[]; overflow: string[]; changed: boolean } {
  let changed = false;
  const c = cells.map((k) => {
    if (k && k !== DENIED && !known(k)) {
      changed = true;
      return null;
    }
    return k;
  });
  const o = overflow.filter((k) => known(k));
  if (o.length !== overflow.length) changed = true;
  return { cells: c, overflow: o, changed };
}

/** 清掉了幾個畫面（格子＋放不下的；第九輪 B9-12 的提示用）。 */
export function prunedCount(cells: (string | null)[], overflow: string[], r: { cells: (string | null)[]; overflow: string[] }): number {
  return cells.filter((k, i) => !!k && k !== DENIED && r.cells[i] === null).length + (overflow.length - r.overflow.length);
}

/** 下一批：放不下的攝影機換上牆，牆上的排到 overflow 後面（輪著看完）。 */
export function nextBatch(cells: (string | null)[], overflow: string[]): { cells: (string | null)[]; overflow: string[] } {
  const size = cells.length;
  const out: (string | null)[] = overflow.slice(0, size);
  while (out.length < size) out.push(null);
  return { cells: out, overflow: [...overflow.slice(size), ...cells.filter((k): k is string => !!k && k !== DENIED)] };
}

/**
 * 每台有連線的設備（NVR / station 整台，不分通道）同時觀看的上限：讀 façade 的 live_limit（平台 media hub 的上限——
 * 多路設備 = 路數 × 2（主 + 子碼流）、單路攝影機 4；vms-system §5.1，Eddy 2026-09-28）。
 * façade 沒帶（問不到）才用這個保守值（nvrd lsrtc 舊的 max_sessions = 2）。
 */
export const DEFAULT_LINK_CAP = 2;

/** 攝影機所在設備的同時觀看上限（façade live_limit；沒有就 fallback）。 */
export function capOf(c: VmsDevice, fallback = DEFAULT_LINK_CAP): number {
  return c.liveLimit && c.liveLimit > 0 ? c.liveLimit : fallback;
}

/** 攝影機所屬的「有連線的設備」：通道 → parent，獨立攝影機 → 自己。 */
export function linkOf(c: VmsDevice): string {
  return `${c.homeId}/${c.parentAid ?? c.aid}`;
}

/** 填滿的順序（SPEC LIVE-01〔填滿〕）：設備與通道都在線的先排，離線的排到最後（格子標灰），不會把離線通道排在前面。 */
export function fillOrder(cameras: VmsDevice[]): VmsDevice[] {
  return [...cameras.filter((c) => c.presence === 'online'), ...cameras.filter((c) => c.presence !== 'online')];
}

/** 用攝影機填滿：在線的先放，每台設備不超過它的上限（capOf）；還有空格才放離線的（不開連線、標灰）。 */
export function autoFill(cameras: VmsDevice[], size: number, fallbackCap = DEFAULT_LINK_CAP): (string | null)[] {
  const used = new Map<string, number>();
  const picked: string[] = [];
  for (const c of fillOrder(cameras)) {
    if (picked.length >= size) break;
    if (c.presence === 'online') {
      const n = used.get(linkOf(c)) ?? 0;
      if (n >= capOf(c, fallbackCap)) continue;
      used.set(linkOf(c), n + 1);
    }
    picked.push(c.key);
  }
  return resize(picked, size);
}

/** 依版面分頁（SPEC #org-tree-ui 第 1 點：拖節點到影像牆 = 依版面自動分頁，不一次開上百路）：第 page 頁的攝影機填進牆。 */
export function fillPage(cameras: VmsDevice[], size: number, page: number, fallbackCap = DEFAULT_LINK_CAP): (string | null)[] {
  return autoFill(fillOrder(cameras).slice(page * size), size, fallbackCap);
}

export function pageCount(cameras: VmsDevice[], size: number): number {
  return Math.max(1, Math.ceil(cameras.length / Math.max(1, size)));
}

/** 工具列常用的版型（LIVE-01：1 / 4 / 9 / 16），其餘與自訂收進「更多版型 ▾」。 */
export const MAIN_GRIDS: GridSize[] = [1, 4, 9, 16];

export function LivePage() {
  const { t } = useTranslation();
  const { tree, cameras: allCameras, loading, facadeLoading } = useDevices();
  // 影像牆只放這台現在有 media.live 的（「我的設備」的 actions，SPEC #device-permissions-ui）；其他的列出原因
  const cameras = useMemo(() => allCameras.filter((c) => can(c, 'media.live')), [allCameras]);
  const noLive = useMemo(() => allCameras.filter((c) => !can(c, 'media.live')), [allCameras]);
  /** 快照下載要 recording.export（LIVE-08；只是介面把關，伺服器沒有這條路徑）。 */
  const canExport = (cam: VmsDevice) => can(cam, 'recording.export');
  /** 對講入口（LIVE-07）：有對講能力（獨立設備 kinio_audio、通道 abilities.talk；lib/caps.ts）、而且這台給了 media.talk（伺服器另外驗）。 */
  const canTalk = (cam: VmsDevice) => controlCaps(cam).talk && can(cam, 'media.talk');
  const [layout, setLayout] = useState<SavedLayout | null>(() => loadLayout());
  const location = useLocation();
  const nav = useNavigate();
  const { setScope, scope, scopeSeq, scopeLabel, scopeLoading, visibleDevices, homes: allHomes, devices: allMine, tree: scopeTree } = useScope();
  const mycamHost = !!useApp().config.mycam?.active;
  // 組織樹的節點 / 群組進來（拖進牆、〔在影像牆開啟〕= 直接填滿）：範圍載好後依版面填第 1 頁。
  // 已經在影像牆上再點一次也要填（B-05：只在第一次掛上時讀 state，在牆上點〔在影像牆開啟〕不會填）
  const [pendingFill, setPendingFill] = useState<boolean>(() => !!(location.state as { fillScope?: boolean } | null)?.fillScope);
  useEffect(() => {
    if (!(location.state as { fillScope?: boolean } | null)?.fillScope) return;
    setPendingFill(true);
    nav(location.pathname + location.search, { replace: true, state: null }); // 重新整理不再填一次
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps
  // 左欄「檢視」分頁點了存過的版面（state.loadLayout = layout_id）：版面清單讀到就載入（第二輪 R2-P1-8）
  const { layouts: serverLayouts } = useServerLayouts();
  const [pendingLayout, setPendingLayout] = useState<string | null>(() => (location.state as { loadLayout?: string } | null)?.loadLayout ?? null);
  // 目前牆是從哪個存過的版面載入的（〔儲存〕覆寫、🗑 刪除用；第三輪 R3-P1-9：套用版面一律在左欄「檢視」）
  const [currentLayout, setCurrentLayout] = useState<string | null>(null);
  useEffect(() => {
    const id = (location.state as { loadLayout?: string } | null)?.loadLayout;
    if (!id) return;
    setPendingLayout(id);
    nav(location.pathname + location.search, { replace: true, state: null });
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pendingLayout) return;
    const l = serverLayouts.find((x) => x.layout_id === pendingLayout);
    if (!l) return;
    setPendingLayout(null);
    setCurrentLayout(l.layout_id);
    const w = layoutWall(l);
    setPromoted(new Set());
    setLayout({ size: w.wall.size, cols: w.wall.custom ? w.wall.cols : undefined, cells: resize(w.cells, w.wall.size), tour: w.tour, overflow: [], page: undefined });
  }, [pendingLayout, serverLayouts]); // eslint-disable-line react-hooks/exhaustive-deps
  const [page, setPage] = useState(0);
  const [focus, setFocus] = useState<string | null>(() => (location.state as { focus?: string } | null)?.focus ?? null);
  // 牆上有、但不在目前範圍的攝影機（切範圍不清空牆，P0-5 ④）：直接查那幾個家戶來播
  const scopeKeys = useMemo(() => new Set(allCameras.map((c) => c.key)), [allCameras]);
  const outKeys = [...(layout?.cells ?? []), ...(layout?.overflow ?? [])].filter((k): k is string => !!k && k !== DENIED && !scopeKeys.has(k));
  const outHomesKey = [...new Set(outKeys.map((k) => k.split('/')[0]))].sort().join(',');
  const outHomes = useMemo(() => {
    const ids = new Set(outHomesKey ? outHomesKey.split(',') : []);
    return allHomes.filter((h) => ids.has(h.home_id));
  }, [outHomesKey, allHomes]);
  const outMine = useMemo(() => {
    const ids = new Set(outHomes.map((h) => h.home_id));
    return allMine.filter((d) => ids.has(d.home_id));
  }, [outHomes, allMine]);
  const outside = useDevicesIn(outHomes, outMine);
  const byKey = useMemo(
    () => new Map([...outside.cameras.filter((c) => can(c, 'media.live')), ...cameras].map((c) => [c.key, c])),
    [cameras, outside.cameras],
  );
  const allTree = useMemo(() => (outside.tree.length ? [...tree, ...outside.tree] : tree), [tree, outside.tree]);
  // 使用者切範圍（scopeSeq 變了；載入時從瀏覽器記住的範圍還原不算）：牆上有攝影機就問要不要用新範圍填滿（不清空）；牆是空的就直接填
  const lastSeq = useRef(scopeSeq);
  const [scopeAsk, setScopeAsk] = useState(false);
  useEffect(() => {
    if (lastSeq.current === scopeSeq) return;
    lastSeq.current = scopeSeq;
    if (pendingFill || (location.state as { fillScope?: boolean } | null)?.fillScope) return;
    const hasCams = (layout?.cells ?? []).some((k) => !!k && k !== DENIED);
    if (hasCams) setScopeAsk(true);
    else setPendingFill(true);
  }, [scopeSeq]); // eslint-disable-line react-hooks/exhaustive-deps
  const [promoted, setPromoted] = useState<Set<number>>(new Set());
  // LIVE-03 解碼預算整台電腦共用（LIVE-10）：先開的視窗用掉的先扣
  const fullBudget = useMemo(() => decodeBudget(), []);
  useMultiScreenVersion();
  const budget = multiScreen.budgetFor(fullBudget);
  const alarmHere = useIsAlarmWindow();
  const others = multiScreen.peerList().length;

  useEffect(() => {
    if (!layout && !loading && !facadeLoading && cameras.length) {
      // 等 façade 的 live_limit，免得先用保守值填了又不變
      const n = autoFill(cameras, 16).filter(Boolean).length;
      const size: GridSize = n <= 1 ? 1 : n <= 4 ? 4 : n <= 9 ? 9 : 16;
      setLayout({ size, cells: autoFill(cameras, size) });
    }
  }, [layout, loading, facadeLoading, cameras]);

  useEffect(() => {
    if (layout) saveLayout(layout);
  }, [layout]);

  useEffect(() => {
    // 遠端範圍（節點 / 檢視 / 群組）要等伺服器展開完、家戶資料回來才有攝影機（B-05）
    if (!pendingFill || scopeLoading || loading || facadeLoading) return;
    setPendingFill(false);
    setScopeAsk(false);
    setPage(0);
    const size = layout?.size ?? 16;
    setLayout((l) => ({ size: l?.size ?? 16, cols: l?.cols, cells: fillPage(cameras, size, 0), tour: l?.tour ?? null, overflow: [], page: 0 }));
  }, [pendingFill, scopeLoading, loading, facadeLoading, cameras, layout?.size]);

  // 載入完（範圍、façade、牆上其他家戶都回來）才清「已不在我能看的清單裡」的格子（權限沒了、被移除）；
  // 不在目前範圍但還看得到的照樣留著（切範圍不清空牆，SPEC LIVE-01 格位規則）
  const myKeys = useMemo(() => {
    const m = new Set<string>();
    for (const d of allMine) {
      m.add(`${d.home_id}/${d.device_ref}`);
      for (const c of d.children ?? []) m.add(`${d.home_id}/${c.device_ref}`);
    }
    return m;
  }, [allMine]);
  const settled = !loading && !facadeLoading && !scopeLoading && !outside.loading && allMine.length > 0;
  // 清掉的格子要說（第九輪 B9-12：已不在 lab 的攝影機被默默從存好的牆上拿掉）：「N 個畫面已不在…」〔復原〕〔知道了〕；
  // 〔復原〕放回原位、這次瀏覽不再自動清（格子寫「這台攝影機已不在你的清單」，可以 ✕ 關掉）
  const [pruned, setPruned] = useState<{ cells: (string | null)[]; overflow: string[]; n: number } | null>(null);
  const [keepGone, setKeepGone] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (!settled || !layout) return;
    const r = pruneWall(layout.cells, layout.overflow ?? [], (k) => keepGone.has(k) || byKey.has(k) || myKeys.has(k));
    if (r.changed) {
      setPruned({ cells: layout.cells, overflow: layout.overflow ?? [], n: prunedCount(layout.cells, layout.overflow ?? [], r) });
      setLayout((l) => (l ? { ...l, cells: r.cells, overflow: r.overflow } : l));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled, myKeys]);
  const undoPrune = () => {
    if (!pruned) return;
    const gone = [...pruned.cells, ...pruned.overflow].filter((k): k is string => !!k && k !== DENIED && !byKey.has(k) && !myKeys.has(k));
    setKeepGone((g) => new Set([...g, ...gone]));
    setLayout((l) => (l ? { ...l, cells: pruned.cells, overflow: pruned.overflow } : l));
    setPruned(null);
  };

  const wall: Wall = wallOf(layout?.size ?? 4, layout?.cols);
  const size = wall.size;
  const cells = layout ? resize(layout.cells, size) : resize([], size);
  const overflow = layout?.overflow ?? [];
  /** keepOverflow：拖放 / 關格子 / 放攝影機時，之前放不下的留著；換一整組（填滿、分頁、載入版面）就清掉 */
  const update = (next: (string | null)[], w: Wall = wall, nextOverflow?: string[], nextPage?: number | null) =>
    setLayout((l) => ({
      size: w.size,
      cols: w.custom ? w.cols : undefined,
      cells: next,
      tour: l?.tour ?? null,
      overflow: nextOverflow ?? l?.overflow ?? [],
      page: nextPage === null ? undefined : (nextPage ?? l?.page),
    }));
  const shrinkMemo = useRef<ShrinkMemo | null>(null);
  const switchWall = (w: Wall) => {
    setPromoted(new Set());
    // 選完版型「更多版型」與自訂列 × 欄一起收合，不常駐在工具列（第二輪 B2-21）
    setMoreOpen(false);
    setCustomOpen(false);
    // 換版型 = 保序壓縮 + overflow（「還有 N 個畫面在下一頁」）；切回放得下的版型時照縮小前的格位（含空格）還原（B3-22）。
    // 不再顯示範圍分頁（那是〔填滿〕排出來的頁）
    const r = switchCells(cells, w.size, overflow, shrinkMemo.current);
    shrinkMemo.current = r.memo;
    update(r.cells, w, r.overflow, null);
  };
  // 目前選取的格子（點格子選取）：沒有空格時點樹上的攝影機取代這一格（SPEC LIVE-01 格位規則）
  const [selCell, setSelCell] = useState<number | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  // LIVE-09：警報彈窗的「指定格位」——影像牆在畫面上時，把來源攝影機換到那一格（已在牆上就交換）
  const cellsRef = useRef(cells);
  cellsRef.current = cells;
  const updateRef = useRef(update);
  updateRef.current = update;
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const [flash, setFlash] = useState<number | null>(null);
  const selRef = useRef<number | null>(null);
  selRef.current = selCell;
  useEffect(
    () =>
      registerWallPopup((camKey, cell) => {
        if (cell >= cellsRef.current.length || !byKey.has(camKey)) return false;
        updateRef.current(dropOnCell(cellsRef.current, cell, { cam: camKey }));
        setPromoted((p) => new Set([...p, cell])); // 這一格一定是即時的（不是預覽）
        setFlash(cell);
        setTimeout(() => setFlash((f) => (f === cell ? null : f)), 5_000);
        return true;
      }),
    [byKey],
  );
  useEffect(
    () =>
      onPlaceCamera((key) => {
        if (!byKey.has(key)) return;
        setFocus(null);
        const r = placeWall(cellsRef.current, key, (k) => byKey.has(k), selRef.current);
        setSelCell(r.at);
        setFlash(r.at);
        setTimeout(() => setFlash((f) => (f === r.at ? null : f)), 1_500);
        if (r.cells === cellsRef.current) return; // 已在牆上：只選取、不重複放
        const ov = (layoutRef.current?.overflow ?? []).filter((k) => k !== key);
        updateRef.current(r.cells, undefined, r.bumped ? [r.bumped, ...ov] : ov);
      }),
    [byKey],
  );
  const [customOpen, setCustomOpen] = useState(false);
  // 「更多版型」與自訂列 × 欄：Esc、點外面都收合（全站選單同一套規則，第二輪 B2-03 / B2-21）
  const gridPickRef = useRef<HTMLSpanElement>(null);
  useDismiss(moreOpen || customOpen, () => (setMoreOpen(false), setCustomOpen(false)), gridPickRef);
  const [custom, setCustom] = useState<[number, number]>([wall.cols, Math.max(1, Math.ceil(size / wall.cols))]);
  const [dropAt, setDropAt] = useState<number | null>(null);
  const dropProps = (i: number) => ({
    onDragOver: (e: React.DragEvent) => {
      if (e.dataTransfer.types.includes(DND_CAM) || e.dataTransfer.types.includes(DND_CELL) || e.dataTransfer.types.includes(DND_SCOPE)) {
        e.preventDefault();
        setDropAt(i);
      }
    },
    onDragLeave: () => setDropAt((x) => (x === i ? null : x)),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setDropAt(null);
      const sc = e.dataTransfer.getData(DND_SCOPE);
      if (sc) {
        try {
          const { key, label } = JSON.parse(sc) as { key: string; label: string };
          setScope(key, label);
          setPendingFill(true);
        } catch {
          /* 壞資料不理 */
        }
        return;
      }
      const src = dropSource(e);
      if (src) update(dropOnCell(cells, i, src, (k) => byKey.has(k)));
    },
  });

  const focused = focus ? byKey.get(focus) : undefined;
  // 專注模式（LIVE-14，SPEC #vms-web-live14）：收起主選單、頂列、組織樹與頁標題，格子填滿視窗；記在這台電腦（localStorage）
  const [focusMode, setFocusModeState] = useState<boolean>(() => !multiScreen.popout && readFocusPref());
  const setFocusMode = (on: boolean) => {
    setFocusModeState(on);
    writeFocusPref(on);
  };
  const focusModeRef = useRef(focusMode);
  focusModeRef.current = focusMode;
  // 鍵盤（SPEC #vms-web-973「鍵盤」、P2-3）：1 / 2 / 3 / 4 = 1 / 4 / 9 / 16 格、F = 選取的格子放大（沒選取就整面牆全螢幕）、Esc = 回影像牆
  const keyRef = useRef<(e: KeyboardEvent) => void>(() => undefined);
  keyRef.current = (e) => {
    if (ignoreHotkey(e)) return;
    if (e.key === 'Escape' && focus) {
      setFocus(null);
      return;
    }
    // Esc 離開專注模式（放大單格時先回牆，上面那條）；Shift+F 切換（F 照舊是放大 / 全螢幕）
    if (e.key === 'Escape' && focusModeRef.current) {
      setFocusMode(false);
      return;
    }
    if ((e.key === 'F' || e.key === 'f') && e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey && !multiScreen.popout) {
      e.preventDefault();
      setFocusMode(!focusModeRef.current);
      return;
    }
    if (focus) return;
    const n = GRID_KEYS[e.key];
    if (n) {
      e.preventDefault();
      switchWall(wallOf(n));
      return;
    }
    if (e.key === 'f' || e.key === 'F') {
      e.preventDefault();
      const k = selCell !== null ? cells[selCell] : null;
      if (k && byKey.get(k)) setFocus(k);
      else void document.querySelector<HTMLElement>('.live-page .wall')?.requestFullscreen?.().catch(() => undefined);
    }
  };
  useEffect(() => {
    const h = (e: KeyboardEvent) => keyRef.current(e);
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);
  // 窄螢幕（390 寬，P2-2）影像牆最多 2 欄
  const narrow = useMedia('(max-width: 600px)');
  const scopeNode = scopeTree.find((n) => n.key === scope);
  // 從組織名起的完整路徑（第七輪 B7-16：「範圍已換到 lab」——EddyStudio 與 LivingStyle › lstw 都有 lab）
  const scopePathName = scopeNode
    ? nodePath(scopeTree, scope)
        .filter((n) => n.kind !== 'source')
        .map((n) => scopeNodeName(t, n))
        .filter(Boolean)
        .join(PATH_SEP)
    : '';
  const scopeName = scope === 'all' ? t('scope.hint_all') : scopePathName || (scopeNode ? scopeNodeName(t, scopeNode) : scopeLabel || t('ot.scope_custom'));
  // 放大一路時牆上的格子不卸載、只是藏起來（VMS UX 評審第三輪 B3-22、R3-P0-1 ②）：回牆時沿用原本的串流、不重新連線；
  // 放大畫面是另一條主碼流（LIVE-02：同一路主 / 子碼流各算一個觀看）
  const shownCells = cells;
  // 牆上的格子：在目前範圍的幾格、不在的幾格（第五輪 B5-07）
  const wallKeys = cells.filter((k): k is string => !!k && k !== DENIED && byKey.has(k));
  const wallInScope = new Set(wallKeys.filter((k) => scopeKeys.has(k))).size;
  const wallOutScope = wallKeys.filter((k) => !scopeKeys.has(k)).length;
  const allOnWall = cameras.length > 0 && wallInScope >= cameras.length;
  const pathOf = useDevicePath();
  const cols = narrow ? Math.min(2, wall.cols) : wall.cols;
  const live = liveCells(
    wall,
    cells.map((k) => !!(k && byKey.get(k))),
    budget,
    promoted,
  );
  const previewCount = focused ? 0 : cells.filter((k, i) => k && byKey.get(k) && !live.has(i)).length;
  const liveCount = live.size + (focused ? 1 : 0);
  useEffect(() => multiScreen.setLive(liveCount), [liveCount]);
  useEffect(() => () => multiScreen.setLive(0), []); // 離開影像牆：不再占其他螢幕的解碼預算

  // 專注模式的列數：格子依視窗比例填滿（格數含大格的面積 / 欄數）
  const wallRows = Math.max(1, Math.ceil(shownCells.reduce((n, _, i) => n + (wall.spans[i]?.[0] ?? 1) * (wall.spans[i]?.[1] ?? 1), 0) / Math.max(1, cols)));
  // 空格只在第一格寫提示，其他只留淡框（LIVE-14「空格」：7 個空格每格都寫一樣的話）
  const firstEmpty = shownCells.findIndex((k) => !k);
  // 範圍分頁與〔下一批〕：一般版面各佔一列；專注模式收進上方細列（LIVE-14「上方只留一條細列」——dev 22 路的範圍多一列分頁，影像區只剩 88%）
  const pager = !focused && layout?.page !== undefined && pageCount(cameras, size) > 1 && (
    <div className="row small" data-testid="wall-pages">
      <button className="btn btn-sm" disabled={page === 0} onClick={() => (setPage(page - 1), update(fillPage(cameras, size, page - 1), wall, [], page - 1))}>
        {t('ot.prev_page')}
      </button>
      <span>{t('ot.page_n', { n: page + 1, total: pageCount(cameras, size) })}</span>
      <button className="btn btn-sm" disabled={page + 1 >= pageCount(cameras, size)} onClick={() => (setPage(page + 1), update(fillPage(cameras, size, page + 1), wall, [], page + 1))}>
        {t('ot.next_page')}
      </button>
    </div>
  );
  const overflowRow = !focused && overflow.length > 0 && (
    <p className="row small" data-testid="wall-overflow">
      <button
        className="btn btn-sm"
        onClick={() => {
          const r = nextBatch(cells, overflow);
          setPromoted(new Set());
          update(r.cells, wall, r.overflow);
        }}
        data-testid="wall-overflow-next"
      >
        {t('live.overflow_next')}
      </button>
    </p>
  );
  return (
    <section className={'live-page' + (focusMode ? ' focus-mode' : '')} data-focus-mode={focusMode ? '1' : undefined}>
      <div className="wall-area">
        <div className="toolbar wrap">
          {focusMode ? (
            <>
              <button className="btn btn-sm" onClick={() => setFocusMode(false)} data-testid="live-focus-exit">
                {t('live.focus_exit')}
              </button>
              <span className="muted small" data-testid="live-focus-count">
                {wallCountText(t, cameras.length, wallInScope, overflow.length)}
              </span>
              {pager}
              {overflowRow}
            </>
          ) : (
            <h1 className="page-title" style={{ margin: 0 }}>
              {t('nav.live')}
            </h1>
          )}
          <span className="spacer" />
          {!multiScreen.popout && !focusMode && (
            <button className="btn btn-sm" onClick={() => setFocusMode(true)} title={t('live.focus_tip')} aria-keyshortcuts="Shift+F" data-testid="live-focus">
              {t('live.focus')}
            </button>
          )}
          {!multiScreen.popout && !focusMode && <RestoreButton />}
          {!multiScreen.popout && !focused && !focusMode && layout && <PopoutButton layout={layout} />}
          {others > 0 && !alarmHere && !multiScreen.popout && (
            <button className="btn btn-sm" onClick={() => multiScreen.claimAlarm()} data-testid="ms-claim-alarm-main">
              {t('live.ms_claim_alarm')}
            </button>
          )}
          <span className="grid-pick" ref={gridPickRef}>
          {focused ? (
            <button className="btn" onClick={() => setFocus(null)}>
              {t('live.back_to_wall')}
            </button>
          ) : (
            <div className="seg" role="group" aria-label={t('live.layout')}>
              {MAIN_GRIDS.map((n) => (
                <button
                  key={n}
                  aria-pressed={!wall.custom && size === n}
                  onClick={() => switchWall(wallOf(n))}
                  title={t('live.grid_key', { n, key: gridKeyOf(n) })}
                  aria-keyshortcuts={gridKeyOf(n)}
                >
                  {n}
                </button>
              ))}
              {/* 其餘版型與自訂收進「更多版型 ▾」（LIVE-01、P0-5 ⑥）；目前用的是其中一種時按鈕直接寫它 */}
              <button
                aria-pressed={wall.custom || !MAIN_GRIDS.includes(size as GridSize)}
                aria-expanded={moreOpen}
                onClick={() => setMoreOpen((o) => !o)}
                data-testid="grid-more"
              >
                {wall.custom ? `${wall.cols}×${size / wall.cols}` : !MAIN_GRIDS.includes(size as GridSize) ? String(size) : t('live.more_grids')} ▾
              </button>
            </div>
          )}
          {/* 更多版型是浮在按鈕下方的小面板，不把工具列擠成兩排（第四輪 R4-P1-8） */}
          {!focused && (moreOpen || customOpen) && (
          <div className="grid-more-pop card">
          {!focused && moreOpen && (
            <div className="seg" role="group" aria-label={t('live.more_grids')} data-testid="grid-more-list">
              {GRID_SIZES.filter((n) => !MAIN_GRIDS.includes(n)).map((n) => (
                <button key={n} aria-pressed={!wall.custom && size === n} onClick={() => switchWall(wallOf(n))}>
                  {n}
                </button>
              ))}
              <button aria-pressed={wall.custom} aria-expanded={customOpen} onClick={() => setCustomOpen((o) => !o)}>
                {t('live.custom_grid')}
              </button>
            </div>
          )}
          {!focused && customOpen && (
            <span className="custom-grid" role="group" aria-label={t('live.custom_grid')}>
              <select className="input" aria-label={t('live.custom_cols')} value={custom[0]} onChange={(e) => setCustom([Number(e.target.value), custom[1]])}>
                {[...Array(CUSTOM_MAX).keys()].map((k) => (
                  <option key={k + 1} value={k + 1}>
                    {k + 1}
                  </option>
                ))}
              </select>
              ×
              <select className="input" aria-label={t('live.custom_rows')} value={custom[1]} onChange={(e) => setCustom([custom[0], Number(e.target.value)])}>
                {[...Array(CUSTOM_MAX).keys()].map((k) => (
                  <option key={k + 1} value={k + 1}>
                    {k + 1}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-sm"
                onClick={() => {
                  switchWall(wallOf(custom[0] * custom[1], custom[0]));
                  setCustomOpen(false);
                  setMoreOpen(false);
                }}
              >
                {t('live.custom_apply')}
              </button>
            </span>
          )}
          </div>
          )}
          </span>
          {!focused && !focusMode && (
            <LayoutMenu wall={wall} cells={cells} tour={layout?.tour ?? null} currentId={currentLayout} onCurrent={setCurrentLayout} />
          )}
          {!focused && (
            <span className="focus-hide">
            <TourControl
              wall={wall}
              cameras={cameras}
              tour={layout?.tour ?? null}
              onTour={(tr) => setLayout((l) => (l ? { ...l, tour: tr } : l))}
              onApply={(w, c) => {
                setPromoted(new Set());
                setLayout((l) => ({ size: w.size, cols: w.custom ? w.cols : undefined, cells: resize(c, w.size), tour: l?.tour ?? null, overflow: [], page: undefined }));
              }}
            />
            </span>
          )}
          {!focused && !focusMode && (
            // 範圍內的畫面都已經在牆上：停用並說明（第九輪 B9-12）
            <button
              className="btn"
              disabled={allOnWall}
              title={allOnWall ? t('live.autofill_all_on') : undefined}
              data-testid="live-autofill"
              onClick={() => (setScopeAsk(false), setPage(0), update(fillPage(cameras, size, 0), wall, [], 0))}
            >
              {t('live.autofill')}
            </button>
          )}
        </div>
        <p className="muted small ot-crumb" data-testid="live-crumb">
          {scopeLabel ? scopeLabel + ' · ' : ''}
          {/* N = 畫面數（攝影機 / 通道），樹上每列是設備數：兩種數字（SPEC #org-tree-ui、LIVE-01，Eddy 2026-10-09） */}
          {/* 「這個範圍有 8 個畫面，牆上放了其中 X 個」；牆上有範圍外的格子就說幾格並給〔換成本範圍〕（第五輪 B5-07） */}
          {/* 牆上放不下、有〔下一頁〕時一句話寫清楚：「這一頁 4 個，下一頁還有 5 個」（第十二輪 B12-12：兩句互相矛盾）；單路放大時同一句（第十三輪 B13-05） */}
          <span title={t('ot.you_can_view_hint')} data-testid="live-crumb-count">
            {wallCountText(t, cameras.length, wallInScope, overflow.length)}
          </span>
          {/* 頁首剛切範圍：問要不要換成新範圍，寫在這一行裡、不另起一張卡（第四輪第 15 列 / 第六輪 R6-P1-7：提示條把牆往下推） */}
          {!focused && scopeAsk && (
            <span className="live-scope-ask" role="status" data-testid="live-scope-ask">
              {' · '}
              {/* 範圍內的畫面都已經在牆上：不給〔用這個範圍填滿牆〕（第八輪 R8-P2-5） */}
              {cameras.length > 0 && wallInScope >= cameras.length ? (
                <>
                  {t('live.scope_changed_all_on', { name: scopeName, n: cameras.length })}{' '}
                  <button className="link-btn" onClick={() => setScopeAsk(false)} data-testid="live-scope-keep">
                    {t('live.scope_ack')}
                  </button>
                </>
              ) : (
                <>
                  {t('live.scope_changed', { name: scopeName, n: cameras.length })}{' '}
                  <button className="link-btn" onClick={() => setPendingFill(true)} data-testid="live-scope-fill">
                    {t('live.scope_fill')}
                  </button>{' '}
                  <button className="link-btn" onClick={() => setScopeAsk(false)} data-testid="live-scope-keep">
                    {t('live.scope_keep')}
                  </button>
                </>
              )}
            </span>
          )}
          {!focused && !scopeAsk && wallOutScope > 0 && (
            <>
              {' · '}
              <span data-testid="wall-out-of-scope">{t('live.wall_out_of_scope', { count: wallOutScope })}</span>{' '}
              <button className="link-btn" onClick={() => (setScopeAsk(false), setPage(0), update(fillPage(cameras, size, 0), wall, [], 0))} data-testid="wall-out-of-scope-fill">
                {t('live.wall_fill_scope')}
              </button>
            </>
          )}
        </p>
        {pruned && pruned.n > 0 && (
          <p className="notice small" role="status" data-testid="wall-pruned">
            {t('live.wall_pruned', { count: pruned.n })}{' '}
            <button className="link-btn" onClick={undoPrune} data-testid="wall-pruned-undo">
              {t('live.wall_pruned_undo')}
            </button>{' '}
            <button className="link-btn" onClick={() => setPruned(null)} data-testid="wall-pruned-ok">
              {t('live.wall_pruned_ok')}
            </button>
          </p>
        )}
        {loading && <p className="muted">{t('common.loading')}</p>}
        {!loading && cameras.length === 0 && <p className="muted">{visibleDevices.length ? t('live.no_cameras') : t('ot.live_empty')}</p>}
        {/* MyCam 主機：用舊手機加入（SPEC #mycam-bind；家的 owner / admin 才有） */}
        {mycamHost && allHomes.some((h) => h.role === 'owner' || h.role === 'admin') && (
          <Link className="btn btn-primary" to="/mycam/add" data-testid="mycam-add-old-phone">
            {t('mycam.add_old_phone')}
          </Link>
        )}
        {noLive.length > 0 && <NoLiveNote cams={noLive} />}
        {/* 範圍分頁只在〔填滿〕排出來的牆上出現（P0-5：換版型後的「放不下」走下面的 overflow，不再出現看不懂的「第 1 / 2 頁」） */}
        {!focusMode && pager}
        {!focusMode && overflowRow}
        {/* 剛開頁面握手中（預算暫時 0）不寫「上限 0 路／其他螢幕已用掉」——第三輪 390 / 1512 截圖看到的誤導字 */}
        {previewCount > 0 && !multiScreen.warming() && <p className="muted small">{t('live.budget_note', { budget, n: previewCount })}</p>}
        {previewCount > 0 && !multiScreen.warming() && budget < fullBudget && (
          <p className="muted small" data-testid="ms-budget-shared">
            {t('live.ms_budget_shared', { used: fullBudget - budget, total: fullBudget })}
          </p>
        )}
        {focused && (
          <div className="wall wall-focus" style={{ gridTemplateColumns: '1fr' }} data-testid="wall-focus">
            <LiveTile
              key={`focus:${focused.key}`}
              cam={focused}
              parentPresence={focused.parentAid ? allTree.find((d) => d.homeId === focused.homeId && d.aid === focused.parentAid)?.presence : undefined}
              label={fullName(focused, allTree)}
              path={`${focused.homeName} › ${fullName(focused, allTree)}`}
              stream="main"
              startDelayMs={0}
              leaveReason="multiview_leave"
              ptz={controlCaps(focused).ptz}
              talk={canTalk(focused)}
              canSnapshot={canExport(focused)}
              extraActions={<DeviceControls dev={focused} compact />}
            />
          </div>
        )}
        <div
          className="wall"
          style={{
            gridTemplateColumns: focusMode ? `repeat(${cols}, minmax(0, 1fr))` : `repeat(${cols}, 1fr)`,
            ...(focusMode ? { gridTemplateRows: `repeat(${wallRows}, minmax(0, 1fr))` } : {}),
            display: focused ? 'none' : undefined,
          }}
          aria-hidden={focused ? true : undefined}
          data-testid="live-wall"
        >
          {shownCells.map((key, i) => {
            const cam = key ? byKey.get(key) : undefined;
            const [cs, rs] = wall.spans[i] ?? [1, 1];
            const style = cs > 1 || rs > 1 ? { gridColumn: `span ${cs}`, gridRow: `span ${rs}` } : undefined;
            if (key === DENIED) {
              return (
                <div key={`denied-${i}`} className="tile tile-empty tile-denied" style={style} data-denied-cell={i}>
                  <span className="muted">{t('layouts.no_permission')}</span>
                </div>
              );
            }
            if (!cam && key && keepGone.has(key)) {
              return (
                <div key={`gone-${i}`} className="tile tile-empty tile-denied" style={style} data-cell={i} data-gone-cell={i} {...dropProps(i)}>
                  <span className="muted">{t('live.cell_gone')}</span>
                  <button
                    className="btn btn-sm"
                    aria-label={t('live.remove')}
                    title={t('live.remove')}
                    onClick={() => {
                      const next = [...cells];
                      next[i] = null;
                      update(next);
                    }}
                  >
                    ✕
                  </button>
                </div>
              );
            }
            if (!cam && key && settled) {
              // 找不到能看即時影像的設備（舊版面存了 Kinio Node 等非攝影機、或權限被收回）：說清楚並可 ✕ 移除（tkt-01M59VTMN…）
              return (
                <div key={`nolive-${i}`} className="tile tile-empty tile-denied" style={style} data-cell={i} data-nolive-cell={i} {...dropProps(i)}>
                  <span className="muted">{t('live.cell_no_live')}</span>
                  <button
                    className="btn btn-sm"
                    aria-label={t('live.remove')}
                    title={t('live.remove')}
                    onClick={() => {
                      const next = [...cells];
                      next[i] = null;
                      update(next);
                    }}
                  >
                    ✕
                  </button>
                </div>
              );
            }
            if (!cam) {
              return (
                <div
                  key={`empty-${i}`}
                  className={`tile tile-empty${dropAt === i ? ' drop-target' : ''}`}
                  style={style}
                  data-cell={i}
                  {...dropProps(i)}
                >
                  {i === firstEmpty && <span className="muted">{t(focusMode ? 'live.empty_cell_focus' : 'live.empty_cell')}</span>}
                </div>
              );
            }
            return (
              <LiveTile
                // 換版型時同一台不重新連線（P0-5 ⑤）：key 用攝影機，同一台出現兩次才加格位
                key={shownCells.indexOf(key) === i ? cam.key : `${cam.key}-${i}`}
                cam={cam}
                leaveReason="multiview_leave"
                parentPresence={cam.parentAid ? allTree.find((d) => d.homeId === cam.homeId && d.aid === cam.parentAid)?.presence : undefined}
                label={fullName(cam, allTree)}
                path={`${cam.homeName} › ${fullName(cam, allTree)}`}
                selected={selCell === i}
                onSelect={() => setSelCell(i)}
                style={style}
                stream={streamFor(wall, i, false)}
                dnd={{ cell: i, over: dropAt === i || flash === i, dataType: DND_CELL, ...dropProps(i) }}
                preview={!live.has(i)}
                onPromote={() => setPromoted((p) => new Set([...p, i]))}
                startDelayMs={(i % 16) * 400}
                onExpand={() => setFocus(cam.key)}
                ptz={false}
                talk={false}
                canSnapshot={canExport(cam)}
                outOfScope={scopeKeys.has(cam.key) ? undefined : pathOf(cam)}
                onClose={() => {
                  const next = [...cells];
                  next[i] = null;
                  update(next);
                }}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** 看得到設備、但現在不能看即時影像的攝影機：逐台列原因（例：只在警報後 30 分鐘內可看、需要開啟兩步驟驗證）。 */
function NoLiveNote({ cams }: { cams: VmsDevice[] }) {
  const { t, i18n } = useTranslation();
  return (
    <details className="small muted no-live" data-testid="no-live">
      <summary>{t('live.no_live_n', { count: cams.length })}</summary>
      <ul className="plain-list">
        {cams.map((c) => (
          <li key={c.key}>
            {c.name} — {whyNot(t, c.access, 'media.live', i18n.language)}
          </li>
        ))}
      </ul>
    </details>
  );
}

/** 專注模式的偏好（LIVE-14）：工作站設定，存這台電腦的瀏覽器；讀寫失敗（無痕、被擋）當沒開。 */
const FOCUS_PREF_KEY = 'vms.live.focus';
function readFocusPref(): boolean {
  try {
    return window.localStorage.getItem(FOCUS_PREF_KEY) === '1';
  } catch {
    return false;
  }
}
function writeFocusPref(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(FOCUS_PREF_KEY, '1');
    else window.localStorage.removeItem(FOCUS_PREF_KEY);
  } catch {
    // 存不了就只在這次有效
  }
}
